/**
 * Shared definition of the translation catalog (.xlsx) exchanged with the client,
 * plus the generator that builds one from scratch.
 *
 * The catalog is generated from `i18n_KEY_FILES` rather than from the
 * `src/locale` directory on purpose: `applyLocaleOverrides` can only resolve
 * main keys registered there, so a key living in an unregistered file would
 * ship in the catalog and then fail at runtime. The page reports that gap
 * separately instead of silently widening the catalog.
 *
 * Kept free of Vite-only syntax so it stays runnable under Jest.
 */

import type { Cell, CellValue, Workbook, Worksheet, Row, Fill, Borders, Font } from "exceljs";
import { getAllFiles } from "@config/i18n/files";
import {
  GLOSSARY_KEY_PREFIX,
  GLOSSARY_MAIN_KEY,
  resolveI18nextGlossaryReferences,
  toCatalogGlossaryTemplate,
} from "./i18nGlossary";

export type LocaleCode = "en" | "es";

export type LocaleMessages = Record<string, any>;

export type LocalesByCode = Record<LocaleCode, LocaleMessages>;

export interface CatalogRow {
  fullKey: string;
  currentEs: string;
  templateEs: string;
  currentEn: string;
  templateEn: string;
  hasVars: boolean;
  notes: string;
}

export interface CatalogStats {
  totalKeys: number;
  sheetsCount: number;
  varsCount: number;
  /** Main keys rendered with their technical name because VIEW_NAMES has no entry for them. */
  unnamedMainKeys: string[];
}

export interface CatalogResult {
  workbook: Workbook;
  stats: CatalogStats;
}

export const CATALOG_FILE_NAME = "i18n-catalog.xlsx";

export const INSTRUCTIONS_SHEET_NAME = "Instrucciones";

/** Terminology sheet: one row per entity, referenced from any text as `{{glossary:name}}`. */
export const GLOSSARY_SHEET_NAME = "Glosario";

/**
 * Column layout, 1-based. "Plantilla" holds the tokenised form of the text — it is
 * maintained by hand on the business side and is never derived from the locale files.
 */
export const CATALOG_COLUMNS = {
  fullKey: 1,
  currentEs: 2,
  templateEs: 3,
  newEs: 4,
  currentEn: 5,
  templateEn: 6,
  newEn: 7,
  notes: 8,
} as const;

export const CATALOG_HEADERS = [
  "Ruta de la key",
  "Texto actual (ES)",
  "Plantilla (ES)",
  "Nuevo texto (ES)",
  "Texto actual (EN)",
  "Plantilla (EN)",
  "Nuevo texto (EN)",
  "Notas",
];

const COL_WIDTHS = [50, 40, 40, 40, 40, 40, 40, 45];

/** Columns the client is meant to write in, rendered white. */
export const EDITABLE_COLUMNS: number[] = [
  CATALOG_COLUMNS.templateEs,
  CATALOG_COLUMNS.newEs,
  CATALOG_COLUMNS.templateEn,
  CATALOG_COLUMNS.newEn,
];

/**
 * Every note the tooling writes into the "Notas" column. They live here, with the sheet
 * format they belong to, so the same sentence is not spelled twice across modules.
 */
export const NOTES = {
  newKey: "NUEVA - key agregada por el equipo tecnico",
  obsoleteKey: "OBSOLETA - esta key ya no existe en la app, no se aplicara",
  hasVars: "Contiene {{variables}} - no modificar las partes entre llaves",
  draftClean: "PROPUESTA AUTOMATICA (glosario) - revisar y confirmar",
  draftNeedsReview: "PROPUESTA AUTOMATICA (glosario) - REVISAR CONCORDANCIA de genero y numero",
  manual: "AFECTADA POR EL GLOSARIO - redactar a mano",
  coveredByGlossary:
    "CUBIERTA POR EL GLOSARIO - el termino se cambia una sola vez en la hoja Glosario",
  malformedTemplate: "PLANTILLA MAL FORMADA - referencia de glosario anidada o sin cerrar",
  repairedTemplate: "PLANTILLA CORREGIDA - se desanido una referencia de glosario duplicada",
  authoredResolved: "REFERENCIAS RESUELTAS sobre el texto que escribio el cliente",
  authoredResolvedReview: "REFERENCIAS RESUELTAS - REVISAR",
} as const;

export const LOCALES: LocaleCode[] = ["es", "en"];

/** Where each locale's texts live, so every module stops declaring its own column map. */
export const COLUMNS_BY_LOCALE: Record<
  LocaleCode,
  { current: number; template: number; newText: number }
> = {
  es: {
    current: CATALOG_COLUMNS.currentEs,
    template: CATALOG_COLUMNS.templateEs,
    newText: CATALOG_COLUMNS.newEs,
  },
  en: {
    current: CATALOG_COLUMNS.currentEn,
    template: CATALOG_COLUMNS.templateEn,
    newText: CATALOG_COLUMNS.newEn,
  },
};

/** Human readable sheet name per main key. Missing entries fall back to the raw key. */
export const VIEW_NAMES: Record<string, string> = {
  locale: "General - Selector de idioma",
  theme: "General - Selector de tema",
  "calendar-view": "Cambiar componente",
  "edit-tag-manually": "Edicion de etiqueta",
  header: "General - Encabezados",
  "search-results": "General - Resultados",
  "empty-results": "General - Estados vacios",
  "package-link-management": "Gestion de ligas y paquetes",
  bundle: "Bundle - Liga",
  "advanced-search": "Busqueda avanzada",
  "create-group-link": "Creacion de ligas",
  "create-package": "Creacion de paquetes",
  "autocomplete-search": "General - Autocompletado",
  "assignation-edit": "Edicion de asignacion",
  "day-list": "Dias de la semana",
  home: "Pagina de inicio",
  "sidebar-menu": "Menu lateral",
  generals: "General - Compartidos",
  dimension: "Dimensiones",
  "user-menu": "Menu de usuario",
  "package-and-group-report": "Reporte paquetes y grupos",
  "remove-session-holidays": "Eliminar sesiones festivos",
  "link-changes-history": "Historial cambios ligas",
  "context-modal": "Selector de contexto",
  selectors: "Selectores generales",
  "group-changes-history": "Historial cambios grupos",
  "group-management": "Gestion de grupos",
  onboarding: "Onboarding",
  tenant: "Institucion - Tenant",
  booking: "Reservas",
  "groups-manager": "Admin de grupos - cupos",
  catalog: "Catalogo",
  "link-errors": "Errores de ligas",
  report: "Reportes",
  "group-restrictions": "Restricciones de grupo",
  "programming-structures": "Estructuras de programacion",
  "programming-edit": "Edicion de programacion",
  "group-references": "Referencias de grupo",
  "microprogramming-groups-report": "Reporte grupos microprog.",
  "agent-chat": "Chat del asistente",
};

/**
 * i18next interpolation variables. Glossary references share the `{{...}}` shape but
 * are resolved by the catalog tooling, never by i18next, so they are matched apart.
 */
const INTERPOLATION_PATTERN = /\{\{(?!glossary:)[^{}]*?\}\}/g;

const THIN_BORDER: Partial<Borders> = {
  top: { style: "thin", color: { argb: "FFD0D0D0" } },
  bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
  left: { style: "thin", color: { argb: "FFD0D0D0" } },
  right: { style: "thin", color: { argb: "FFD0D0D0" } },
};

const HEADER_FILL: Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2B3A4E" } };
const READ_ONLY_FILL: Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
const EDITABLE_FILL: Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
const WARNING_FILL: Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3CD" } };
const HEADER_FONT: Partial<Font> = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };

/** Flattens nested messages into dot-separated paths: `{ a: { b: "x" } }` -> `{ "a.b": "x" }`. */
export function flattenMessages(messages: LocaleMessages, prefix = ""): Record<string, string> {
  const result: Record<string, string> = {};

  Object.entries(messages ?? {}).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(result, flattenMessages(value, fullKey));
    } else {
      result[fullKey] = String(value ?? "");
    }
  });

  return result;
}

function flattenRawLocales(locales: LocalesByCode): Record<LocaleCode, Record<string, string>> {
  return { es: flattenMessages(locales.es), en: flattenMessages(locales.en) };
}

/** Template view of native `$t(glossary...)` references used inside locale JSON files. */
export function getCatalogGlossaryTemplates(
  locales: LocalesByCode,
): Record<LocaleCode, Record<string, string>> {
  const raw = flattenRawLocales(locales);

  const templatesFor = (locale: LocaleCode): Record<string, string> =>
    Object.fromEntries(
      Object.entries(raw[locale])
        .filter(([fullKey]) => !fullKey.startsWith(GLOSSARY_KEY_PREFIX))
        .map(([fullKey, value]) => [fullKey, toCatalogGlossaryTemplate(value)])
        .filter(([, template]) => !!template),
    );

  return { es: templatesFor("es"), en: templatesFor("en") };
}

export function extractInterpolationVars(value: string): string[] {
  return (value?.match(INTERPOLATION_PATTERN) ?? []).sort();
}

export function hasInterpolationVars(value: string): boolean {
  return extractInterpolationVars(value).length > 0;
}

/** Reads every locale bundled in `i18n_KEY_FILES`, merged into one object per language. */
export function getBundledLocales(): LocalesByCode {
  const { enFiles, esFiles } = getAllFiles();

  return { en: enFiles, es: esFiles };
}

/**
 * Flattened app texts as users see them: glossary definitions are omitted from the normal
 * key catalog and native i18next nesting is resolved to its current term.
 */
export function flattenLocales(locales: LocalesByCode): Record<LocaleCode, Record<string, string>> {
  const raw = flattenRawLocales(locales);

  const resolvedFor = (locale: LocaleCode): Record<string, string> =>
    Object.fromEntries(
      Object.entries(raw[locale])
        .filter(
          ([fullKey]) => fullKey !== GLOSSARY_MAIN_KEY && !fullKey.startsWith(GLOSSARY_KEY_PREFIX),
        )
        .map(([fullKey, value]) => [fullKey, resolveI18nextGlossaryReferences(value, raw[locale])]),
    );

  return { es: resolvedFor("es"), en: resolvedFor("en") };
}

/** Rows that seed the dedicated workbook sheet from the locale source of truth. */
export function buildGlossaryRows(locales: LocalesByCode): CatalogRow[] {
  const raw = flattenRawLocales(locales);
  const glossaryKeys = [
    ...new Set(
      [...Object.keys(raw.es), ...Object.keys(raw.en)].filter(fullKey =>
        fullKey.startsWith(GLOSSARY_KEY_PREFIX),
      ),
    ),
  ].sort();

  return glossaryKeys.map(fullKey => {
    const category = fullKey.split(".")[1];

    return {
      fullKey,
      currentEs: raw.es[fullKey] ?? "",
      templateEs: "",
      currentEn: raw.en[fullKey] ?? "",
      templateEn: "",
      hasVars: false,
      notes: category === "entities" ? "Entidades" : category === "time" ? "Tiempo" : "Glosario",
    };
  });
}

/** Excel rejects `\ / * ? : [ ]` and names longer than 31 chars. */
export function sanitizeSheetName(name: string): string {
  return name.replace(/[\\/*?:[\]]/g, "").substring(0, 31);
}

/** Truncation can collapse two different main keys into the same name, which Excel rejects. */
export function toUniqueSheetName(name: string, usedNames: Set<string>): string {
  if (!usedNames.has(name)) {
    usedNames.add(name);

    return name;
  }

  let suffix = 2;

  while (usedNames.has(`${name.substring(0, 28)} (${suffix})`)) suffix++;

  const uniqueName = `${name.substring(0, 28)} (${suffix})`;
  usedNames.add(uniqueName);

  return uniqueName;
}

export function getSheetNameForMainKey(mainKey: string, usedNames: Set<string>): string {
  return toUniqueSheetName(sanitizeSheetName(VIEW_NAMES[mainKey] ?? mainKey), usedNames);
}

/** The one place a catalog row is derived from the locale files. */
export function buildCatalogRow(
  fullKey: string,
  flat: Record<LocaleCode, Record<string, string>>,
  templates: Record<LocaleCode, Record<string, string>>,
  extraNote = "",
): CatalogRow {
  const currentEs = flat.es[fullKey] ?? "";
  const currentEn = flat.en[fullKey] ?? "";
  const hasVars = hasInterpolationVars(currentEs) || hasInterpolationVars(currentEn);
  const notes = [extraNote, hasVars ? NOTES.hasVars : ""].filter(Boolean).join(" | ");

  return {
    fullKey,
    currentEs,
    templateEs: templates.es[fullKey] ?? "",
    currentEn,
    templateEn: templates.en[fullKey] ?? "",
    hasVars,
    notes,
  };
}

/** The 8 cells of a catalog row, in sheet order. The client's columns start out empty. */
export function toSheetValues(row: CatalogRow): string[] {
  return [
    row.fullKey,
    row.currentEs,
    row.templateEs,
    "",
    row.currentEn,
    row.templateEn,
    "",
    row.notes,
  ];
}

/** Groups every key of both locales under its main key (the first segment of the path). */
export function buildCatalogRows(locales: LocalesByCode): Record<string, CatalogRow[]> {
  const flat = flattenLocales(locales);
  const templates = getCatalogGlossaryTemplates(locales);
  const allKeys = [...new Set([...Object.keys(flat.es), ...Object.keys(flat.en)])].sort();
  const rowsByMainKey: Record<string, CatalogRow[]> = {};

  allKeys.forEach(fullKey => {
    const mainKey = fullKey.split(".")[0];

    if (!rowsByMainKey[mainKey]) rowsByMainKey[mainKey] = [];

    rowsByMainKey[mainKey].push(buildCatalogRow(fullKey, flat, templates));
  });

  return rowsByMainKey;
}

/** Derived from the rows alone, so the page can show the totals without loading exceljs. */
export function getCatalogStats(rowsByMainKey: Record<string, CatalogRow[]>): CatalogStats {
  const mainKeys = Object.keys(rowsByMainKey);
  const rows = Object.values(rowsByMainKey).flat();

  return {
    totalKeys: rows.length,
    varsCount: rows.filter(row => row.hasVars).length,
    sheetsCount: mainKeys.length,
    unnamedMainKeys: mainKeys.filter(mainKey => !VIEW_NAMES[mainKey]).sort(),
  };
}

export function applyHeaderStyle(row: Row): void {
  row.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.border = THIN_BORDER;
    cell.alignment = { vertical: "middle", wrapText: true };
  });

  row.height = 28;
}

export function applyDataRowStyle(row: Row, hasVars: boolean): void {
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.border = THIN_BORDER;
    cell.alignment = { vertical: "middle", wrapText: true };

    if (EDITABLE_COLUMNS.includes(colNumber)) {
      cell.fill = EDITABLE_FILL;
    } else if (hasVars) {
      cell.fill = WARNING_FILL;
    } else {
      cell.fill = READ_ONLY_FILL;
    }
  });
}

/** Applies the shared column widths, header styling, autofilter and frozen header. */
export function initCatalogSheet(sheet: Worksheet): void {
  sheet.columns = CATALOG_HEADERS.map((header, index) => ({
    header,
    key: `col${index}`,
    width: COL_WIDTHS[index],
  }));

  applyHeaderStyle(sheet.getRow(1));

  sheet.autoFilter = { from: "A1", to: "H1" };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
}

function addInstructionsSheet(sheet: Worksheet): void {
  sheet.mergeCells("A1:F1");

  const titleCell = sheet.getCell("A1");
  titleCell.value = "Guía para personalizar textos de la aplicación";
  titleCell.font = { bold: true, size: 16, color: { argb: "FF2B3A4E" } };
  titleCell.alignment = { vertical: "middle" };
  sheet.getRow(1).height = 36;

  const instructions = [
    "",
    "Cada pestaña de este archivo corresponde a una sección o vista de la aplicación.",
    'La pestaña "Glosario" define la terminología de la institución.',
    "",
    "Columnas del catálogo:",
    '  • "Ruta de la key" → Identificador técnico del texto (NO modificar).',
    '  • "Texto actual (ES)" / "Texto actual (EN)" → Lo que se muestra hoy (referencia).',
    '  • "Plantilla (ES)" / "Plantilla (EN)" → Versión del texto con referencias al glosario,',
    "     escritas como {{glossary:nombre}}. Se mantienen desde el negocio.",
    '  • "Nuevo texto (ES)" / "Nuevo texto (EN)" → Escriba aquí el texto final que desea.',
    '  • "Notas" → Indicaciones especiales sobre el texto.',
    "",
    "Reglas importantes:",
    '  1. El texto que se aplica es SIEMPRE el de las columnas "Nuevo texto".',
    "  2. Deje en blanco las filas que NO desea modificar.",
    '  3. En "Nuevo texto" escriba el texto ya resuelto, sin {{glossary:...}}.',
    "  4. Si un texto contiene elementos como {{count}} o {{week}}, debe mantenerlos",
    "     exactamente igual en su nuevo texto. Son variables dinámicas que la aplicación",
    "     reemplaza automáticamente. Las filas con variables están resaltadas en amarillo.",
    "  5. Si solo usa un idioma, llene únicamente la columna correspondiente.",
    "  6. Cuando termine, envíe este archivo de vuelta al equipo técnico.",
  ];

  instructions.forEach((text, index) => {
    const row = sheet.getRow(index + 2);
    const isSectionTitle = text.startsWith("Columnas") || text.startsWith("Reglas");

    row.getCell(1).value = text;
    row.getCell(1).font = isSectionTitle ? { bold: true, size: 12 } : { size: 11 };
  });

  sheet.getColumn(1).width = 90;
}

/** Cells can hold rich text, formulas or hyperlinks, none of which stringify usefully. */
export function getCellText(cell: Cell): string {
  const value: CellValue = cell?.value;

  if (value === null || value === undefined) return "";

  if (typeof value === "object") {
    if ("richText" in value)
      return value.richText
        .map(part => part.text)
        .join("")
        .trim();
    if ("formula" in value) return String(value.result ?? "").trim();
    if ("text" in value) return String(value.text ?? "").trim();
    if (value instanceof Date) return value.toISOString();

    return "";
  }

  return String(value).trim();
}

/** exceljs ships a UMD browser build, so the namespace shape differs between bundlers. */
export async function loadExcelJs() {
  const module = await import("exceljs");

  return (module as any).default ?? module;
}

export async function buildCatalogWorkbook(locales: LocalesByCode): Promise<CatalogResult> {
  const ExcelJS = await loadExcelJs();
  const rowsByMainKey = buildCatalogRows(locales);
  const sortedMainKeys = Object.keys(rowsByMainKey).sort();
  const workbook: Workbook = new ExcelJS.Workbook();
  const usedSheetNames = new Set<string>();

  workbook.creator = "DarwinEd - i18n Catalog";
  workbook.created = new Date();

  addInstructionsSheet(workbook.addWorksheet(INSTRUCTIONS_SHEET_NAME));
  usedSheetNames.add(INSTRUCTIONS_SHEET_NAME);

  const glossarySheet = workbook.addWorksheet(GLOSSARY_SHEET_NAME);
  initCatalogSheet(glossarySheet);
  buildGlossaryRows(locales).forEach(row => {
    const dataRow = glossarySheet.addRow(toSheetValues(row));

    applyDataRowStyle(dataRow, false);
  });
  usedSheetNames.add(GLOSSARY_SHEET_NAME);

  sortedMainKeys.forEach(mainKey => {
    const sheet = workbook.addWorksheet(getSheetNameForMainKey(mainKey, usedSheetNames));

    initCatalogSheet(sheet);

    rowsByMainKey[mainKey].forEach(row => {
      const dataRow = sheet.addRow(toSheetValues(row));

      applyDataRowStyle(dataRow, row.hasVars);
    });
  });

  return { workbook, stats: getCatalogStats(rowsByMainKey) };
}
